import { ArrowUpRight, ChevronDown, ChevronRight, Search, Sparkles, X } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import logoDark from '../../assets/logo-simple-dark.svg';
import { useTenant } from '../../hooks/useTenant';
import { usePermissions } from '../../hooks/usePermissions';
import { filterNavigation, getDashboardNavigation, navigationItemIsActive } from './navigation';

function navigationKey(item) {
  return item.path || item.label;
}

function workspaceInitial(name = '') {
  return String(name || 'S').trim().charAt(0).toUpperCase();
}

function formatAccountType(value = '') {
  return String(value || 'Learning workspace')
    .replace(/[-_]+/g, ' ')
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function searchNavigation(items = [], query = '') {
  const normalizedQuery = String(query || '').trim().toLowerCase();
  if (!normalizedQuery) return items;

  return items.reduce((matches, item) => {
    const itemMatches = String(item.label || '').toLowerCase().includes(normalizedQuery);
    const matchingChildren = searchNavigation(item.children || [], normalizedQuery);
    if (itemMatches || matchingChildren.length) {
      matches.push({ ...item, children: itemMatches ? item.children : matchingChildren });
    }
    return matches;
  }, []);
}

export function Sidebar({ open, onClose }) {
  const { activeTenant, memberships, switchTenant } = useTenant();
  const { can } = usePermissions();
  const location = useLocation();
  const [expanded, setExpanded] = useState({});
  const [navigationSearch, setNavigationSearch] = useState('');

  const visibleNavigation = useMemo(() => {
    const navigation = getDashboardNavigation(activeTenant?.type);
    return filterNavigation(navigation, can);
  }, [activeTenant?.type, can]);

  const displayedNavigation = useMemo(
    () => searchNavigation(visibleNavigation, navigationSearch),
    [navigationSearch, visibleNavigation],
  );

  useEffect(() => {
    setExpanded((current) => {
      const next = { ...current };
      visibleNavigation.forEach((item) => {
        if (item.children?.length && navigationItemIsActive(item, location.pathname)) {
          next[navigationKey(item)] = true;
        }
      });
      return next;
    });
  }, [location.pathname, visibleNavigation]);

  function renderNavigationItems(items, level = 0) {
    return items.map((item) => {
      const Icon = item.icon;
      const active = navigationItemIsActive(item, location.pathname);

      if (item.children?.length) {
        const key = navigationKey(item);
        const isOpen = navigationSearch ? true : (expanded[key] ?? active);

        return (
          <div key={key} className={`sidebar-group sidebar-group-level-${level} ${isOpen ? 'is-open' : ''} ${active ? 'is-active' : ''}`}>
            <div className="sidebar-group-head">
              <button
                className={`sidebar-link sidebar-group-trigger ${active ? 'active' : ''}`}
                type="button"
                aria-expanded={isOpen}
                onClick={() => setExpanded((current) => ({ ...current, [key]: !isOpen }))}
              >
                {Icon ? <Icon size={18} /> : <i className="sidebar-subnav-dot" />}
                <span>{item.label}</span>
                {isOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
              </button>
            </div>
            {isOpen ? <div className="sidebar-subnav">{renderNavigationItems(item.children, level + 1)}</div> : null}
          </div>
        );
      }

      return (
        <NavLink
          key={item.path}
          to={item.path}
          end={item.end}
          onClick={onClose}
          className={({ isActive }) => `${level === 0 ? 'sidebar-link' : 'sidebar-sublink'} ${isActive ? 'active' : ''}`}
        >
          {level > 0 ? <i className="sidebar-subnav-dot" /> : Icon ? <Icon size={18} /> : null}
          <span>{item.label}</span>
        </NavLink>
      );
    });
  }

  return (
    <>
      <button className={`sidebar-scrim ${open ? 'is-open' : ''}`} type="button" aria-label="Close navigation" onClick={onClose} />
      <aside id="dashboard-navigation" className={`sidebar ${open ? 'is-open' : ''}`} aria-hidden={!open}>
        <div className="sidebar-header">
          <Link className="sidebar-brand" to="/dashboard" onClick={onClose} aria-label="SmartCampus dashboard">
            <span className="sidebar-brand-mark"><img src={logoDark} alt="" aria-hidden="true" /></span>
            <span className="sidebar-brand-copy"><strong>SmartCampus</strong><small>Dashboard</small></span>
          </Link>
          <button className="sidebar-close" type="button" aria-label="Close navigation" onClick={onClose}><X size={17} /></button>
        </div>

        <section className="sidebar-workspace-card" aria-label="Current workspace">
          <span className="sidebar-workspace-avatar" aria-hidden="true">{workspaceInitial(activeTenant?.name)}</span>
          <div>
            <small>Current workspace</small>
            <strong>{activeTenant?.name || 'Your campus'}</strong>
            <span>{formatAccountType(activeTenant?.type)}</span>
          </div>
          <i title="Workspace is active" aria-label="Workspace is active" />
        </section>

        {memberships.length > 1 ? (
          <label className="sidebar-tenant-switcher">
            <span>Switch workspace</span>
            <select value={activeTenant?.id || ''} onChange={(event) => switchTenant(event.target.value)}>
              {memberships.map((membership) => (
                <option key={membership.tenantId} value={membership.tenantId}>{membership.tenant?.name}</option>
              ))}
            </select>
          </label>
        ) : null}

        <label className="sidebar-navigation-search" htmlFor="dashboard-menu-search">
          <Search size={14} aria-hidden="true" />
          <input
            id="dashboard-menu-search"
            type="search"
            placeholder="Find a page"
            value={navigationSearch}
            onChange={(event) => setNavigationSearch(event.target.value)}
          />
          {navigationSearch ? <button type="button" aria-label="Clear menu search" onClick={() => setNavigationSearch('')}><X size={12} /></button> : null}
        </label>

        <nav className="sidebar-nav" aria-label="Dashboard navigation">
          <span className="sidebar-nav-label">Workspace navigation</span>
          {displayedNavigation.length ? renderNavigationItems(displayedNavigation) : (
            <div className="sidebar-navigation-empty"><Search size={15} /><span>No page matches “{navigationSearch}”</span></div>
          )}
        </nav>

        <div className="sidebar-assistant">
          <span className="sidebar-assistant-icon"><Sparkles size={17} /></span>
          <div><strong>Firefly AI</strong><small>Your workspace assistant</small></div>
          <Link to="/platform/firefly" onClick={onClose} aria-label="Open Firefly AI"><ArrowUpRight size={15} /></Link>
        </div>
      </aside>
    </>
  );
}
